import React, { useState } from "react";
import Popover from "@material-ui/core/Popover";
import { makeStyles } from "@material-ui/core";
import { Editor } from "@tldraw/tldraw";
import { Title } from "./NewEffectSidebar";
import Tooltip from "@material-ui/core/Tooltip";
import useTooltipStyles from "./style";

const LINE_ARROW_COLOR_OPTIONS: { value: TLArrowLineColor; hex: string }[] = [
  { value: "black", hex: "#1d1d1d" },
  { value: "grey", hex: "#9fa8b2" },
  { value: "light-violet", hex: "#e085f4" },
  { value: "violet", hex: "#ae3ec9" },
  { value: "blue", hex: "#4465e9" },
  { value: "light-blue", hex: "#4ba1f1" },
  { value: "yellow", hex: "#f1ac4b" },
  { value: "orange", hex: "#e16919" },
  { value: "green", hex: "#099268" },
  { value: "light-green", hex: "#4cb05e" },
  { value: "light-red", hex: "#f87777" },
  { value: "red", hex: "#e03131" },
  { value: "white", hex: "#FFFFFF" },
];

type TLArrowLineColor =
  | "black"
  | "grey"
  | "light-violet"
  | "violet"
  | "blue"
  | "light-blue"
  | "yellow"
  | "orange"
  | "green"
  | "light-green"
  | "light-red"
  | "red"
  | "white";

type TLArrowLineSize = "s" | "m" | "l" | "xl";

const STROKE_SIZE_OPTIONS: { value: TLArrowLineSize; label: string }[] = [
  { value: "s", label: "S" },
  { value: "m", label: "M" },
  { value: "l", label: "L" },
  { value: "xl", label: "XL" },
];

const useStyles = makeStyles(() => ({
  paper: {
    transform: "translateX(-245px) !important",
    borderRadius: "4px",
    padding: "12px",
  },
}));

interface LineArrowColorSidebarProps {
  editor: Editor;
  selectedShape: any;
  setSelectedShape: (shape: any) => void;
}

export function LineArrowColorSidebar({ editor, selectedShape, setSelectedShape }: LineArrowColorSidebarProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [strokeAnchorEl, setStrokeAnchorEl] = useState<HTMLElement | null>(null);
  const classes = useStyles();
  const classesTooltip = useTooltipStyles();

  const currentColor = (selectedShape?.props?.color as TLArrowLineColor) ?? "black";
  const currentHex = LINE_ARROW_COLOR_OPTIONS.find((o) => o.value === currentColor)?.hex ?? "#1d1d1d";
  const colorLabel = currentColor.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

  const currentSize = (selectedShape?.props?.size as TLArrowLineSize) ?? "m";
  const currentSizeLabel = STROKE_SIZE_OPTIONS.find((o) => o.value === currentSize)?.label ?? "M";

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleStrokeOpen = (event: React.MouseEvent<HTMLElement>) => {
    setStrokeAnchorEl(event.currentTarget);
  };

  const handleStrokeClose = () => {
    setStrokeAnchorEl(null);
  };

  const handleColorSelect = (color: TLArrowLineColor) => {
    if (!selectedShape || (selectedShape.type !== "line" && selectedShape.type !== "arrow")) return;
    const updated = {
      ...selectedShape,
      props: { ...selectedShape.props, color },
    };
    editor.updateShape(updated);
    setSelectedShape(updated);
    handleClose();
  };

  const handleStrokeSelect = (size: TLArrowLineSize) => {
    if (!selectedShape || (selectedShape.type !== "line" && selectedShape.type !== "arrow")) return;
    const updated = {
      ...selectedShape,
      props: { ...selectedShape.props, size },
    };
    editor.updateShape(updated);
    setSelectedShape(updated);
    handleStrokeClose();
  };

  const open = Boolean(anchorEl);
  const strokeOpen = Boolean(strokeAnchorEl);

  return (
    <div className="flex flex-col gap-2 w-full">
      <div className="px-4 mb-2">
        <Title name="Color" />
      </div>
      <div className="px-4">
        <Tooltip
          title="Select the color of the line or arrow"
          arrow
          classes={{ tooltip: classesTooltip.tooltip, arrow: classesTooltip.arrow }}
        >
          <div
            className="flex items-center w-full py-1 px-2 rounded-[4px] relative"
            style={{ border: "1px solid #E2E2FC" }}
          >
            <button
              type="button"
              style={{ backgroundColor: currentHex }}
              className="min-w-4 min-h-4 rounded-[4px] mr-[6px] color-picker-border cursor-pointer p-0 border-0"
              aria-label="Open color picker"
              onClick={handleOpen}
            />
            <span className="text-[10px] text-[#2E3860] truncate" style={{ lineHeight: "16px" }}>
              {colorLabel}
            </span>
          </div>
        </Tooltip>
        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          PaperProps={{ className: classes.paper }}
        >
          <div className="flex flex-wrap gap-2" style={{ width: 180 }}>
            {LINE_ARROW_COLOR_OPTIONS.map(({ value, hex }) => (
              <button
                key={value}
                type="button"
                aria-label={`Color ${value}`}
                onClick={() => handleColorSelect(value)}
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: 6,
                  backgroundColor: hex,
                  border: currentColor === value ? "2px solid #2E3860" : "1px solid #E2E2FC",
                  boxShadow: currentColor === value ? "0 0 0 1px #2E3860" : undefined,
                  cursor: "pointer",
                  padding: 0,
                }}
              />
            ))}
          </div>
        </Popover>
      </div>

      <div className="px-4 mb-2 mt-2">
        <Title name="Stroke" />
      </div>
      <div className="px-4">
        <Tooltip
          title="Select the stroke thickness of the line or arrow"
          arrow
          classes={{ tooltip: classesTooltip.tooltip, arrow: classesTooltip.arrow }}
        >
          <div
            className="flex items-center w-full py-1 px-2 rounded-[4px] relative"
            style={{ border: "1px solid #E2E2FC" }}
          >
            <button
              type="button"
              className="min-w-4 min-h-4 rounded-[4px] mr-[6px] flex items-center justify-center cursor-pointer p-0 border-0 bg-[#f5f5f5] color-picker-border"
              aria-label="Open stroke picker"
              onClick={handleStrokeOpen}
            >
              <span
                className="rounded-full bg-[#2E3860] block shrink-0"
                style={{
                  width: 14,
                  height: currentSize === "s" ? 2 : currentSize === "m" ? 3.5 : currentSize === "l" ? 5 : 8,
                }}
              />
            </button>
            <span className="text-[10px] text-[#2E3860] truncate" style={{ lineHeight: "16px" }}>
              {currentSizeLabel}
            </span>
          </div>
        </Tooltip>
        <Popover
          open={strokeOpen}
          anchorEl={strokeAnchorEl}
          onClose={handleStrokeClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          PaperProps={{ className: classes.paper }}
        >
          <div className="flex flex-wrap gap-2" style={{ width: 180 }}>
            {STROKE_SIZE_OPTIONS.map(({ value, label }) => (
              <button
                key={value}
                type="button"
                aria-label={`Stroke ${label}`}
                onClick={() => handleStrokeSelect(value)}
                className="rounded-[4px] px-3 py-2 text-[10px] font-medium cursor-pointer border"
                style={{
                  borderColor: currentSize === value ? "#2E3860" : "#E2E2FC",
                  color: "#2E3860",
                  backgroundColor: currentSize === value ? "rgba(46, 56, 96, 0.08)" : "transparent",
                }}
              >
                {label}
              </button>
            ))}
          </div>
        </Popover>
      </div>
    </div>
  );
}
